import { AccountBalanceOutlined as AccountBalanceOutlinedIcon } from 'components/icons';
import InfoTooltip from 'components/InfoTooltip';
import Loader from 'components/Loader';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { getFilterOptionsFromClassifier, mapToFilterOptions } from 'components/tables/list-table/filterUtil';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import React, { useCallback, useMemo, useState } from 'react';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useLazyGetLawFirmsWithFiltersQuery } from 'store/api/law-firms';
import { RelationshipTypeCode } from 'types/contacts';
import { LawFirmListItemDto } from 'types/law-firms';
import { t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';
import { GroupLink, LastReferralCell, LawFirmPartyCell, MattersCell, ReferralsCell } from './LawFirmCells';
import LawFirmGroupsBlock from './LawFirmGroupsBlock';

const USED_CLASSIFIERS = ['COUNTRY', 'RELATIONSHIP_TYPE', 'SEGMENT_TYPE', 'SERVICE_LEVEL_TYPE', 'KEYWORD_TYPE'];

const GROUP_TOOLTIPS = { 'law-firms.filters.group': 'law-firms.filters.group-tooltip' };

const getLawFirmHref = (lawFirm: LawFirmListItemDto) => `/contact/${lawFirm.party.id}`;

/** A figure column's header, with the tooltip saying whose figures they are. */
const figureHeader = (titleId: string) => () => (
    <span className="inline-flex items-center gap-1">
        {t(titleId)}
        <InfoTooltip text="law-firms.group-figures-tooltip" className="h-4" />
    </span>
);

const LawFirmsTable: React.FC = () => {
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: USED_CLASSIFIERS });
    const [fetchData, { data, isFetching }] = useLazyGetLawFirmsWithFiltersQuery();
    // The contacts whose every group is shown, past the first three.
    const [revealedPartyIds, setRevealedPartyIds] = useState<ReadonlySet<number>>(() => new Set());
    const revealAllGroups = useCallback(
        (partyId: number) => setRevealedPartyIds((ids) => new Set(ids).add(partyId)),
        []
    );

    // A contact in two or more groups draws one block across the row; a cell of these columns only ever
    // shows a contact in one group or in none.
    const renderFullRow = useCallback(
        (lawFirm: LawFirmListItemDto) =>
            lawFirm.partyGroups.length > 1 ? (
                <LawFirmGroupsBlock
                    lawFirm={lawFirm}
                    classifiers={classifiers}
                    expanded={revealedPartyIds.has(lawFirm.party.id)}
                    onShowMore={revealAllGroups}
                />
            ) : null,
        [classifiers, revealedPartyIds, revealAllGroups]
    );

    const columns = useMemo<DataTableColumnDef<LawFirmListItemDto>[]>(
        () => [
            {
                header: t('law-firms.name-segment'),
                accessorKey: 'party',
                enableSorting: false,
                meta: {
                    widthWeight: 1.35,
                    minPx: 240,
                },
                cell: ({ row }) => <LawFirmPartyCell party={row.original.party} classifiers={classifiers} />,
            },
            {
                header: t('law-firms.legal-services-group'),
                id: 'legalServicesGroup',
                enableSorting: false,
                meta: {
                    widthWeight: 1.35,
                    minPx: 240,
                },
                cell: ({ row }) => {
                    const [group] = row.original.partyGroups;
                    return group ? (
                        <GroupLink group={group} />
                    ) : (
                        <div className="flex items-center gap-1">
                            <span className="text-body1 text-fg-secondary">{t('law-firms.no-group')}</span>
                            <InfoTooltip text="law-firms.no-group-tooltip" />
                        </div>
                    );
                },
            },
            {
                header: figureHeader('law-firms.group-cases'),
                id: 'relatedMatters',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 180,
                },
                cell: ({ row }) => <MattersCell figures={row.original} />,
            },
            {
                header: figureHeader('law-firms.group-referrals'),
                id: 'referrals',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 180,
                },
                cell: ({ row }) => <ReferralsCell figures={row.original} />,
            },
            {
                header: figureHeader('law-firms.last-group-referral'),
                id: 'lastGroupReferral',
                enableSorting: false,
                meta: {
                    widthWeight: 1.4,
                    minPx: 200,
                },
                cell: ({ row }) => <LastReferralCell figures={row.original} />,
            },
        ],
        [classifiers]
    );

    const filterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: [
                {
                    selector: 'partyType',
                    filters: [
                        {
                            label: t('law-firms.filters.show-only-companies'),
                            argument: 'COMPANY',
                            defaultSelected: true,
                        },
                    ],
                },
                {
                    type: 'dropdown-multiselect' as const,
                    label: t('law-firms.filters.all-jurisdictions'),
                    selector: 'id',
                    comperator: '=registrationaddresscountry=',
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers['COUNTRY']) : [],
                },
                {
                    type: 'dropdown-multiselect' as const,
                    label: t('law-firms.filters.all-segments'),
                    selector: 'segmentTypeCode',
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers['SEGMENT_TYPE']) : [],
                },
            ],
            filters: [
                // Country filter (duplicates All jurisdictions)
                {
                    type: 'multiselect',
                    label: t('law-firms.filters.country'),
                    selector: 'id',
                    comperator: '=registrationaddresscountry=',
                    group: 'law-firms.filters.country',
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers['COUNTRY']) : [],
                },
                // Contact section filters
                {
                    type: 'text',
                    label: t('law-firms.filters.contact-name'),
                    selector: 'name',
                    group: 'law-firms.filters.contact',
                },
                {
                    type: 'multiselect',
                    label: t('law-firms.filters.contact-related-keyword'),
                    selector: 'id',
                    comperator: '=contactkeyword=',
                    group: 'law-firms.filters.contact',
                    options: classifiers
                        ? getFilterOptionsFromClassifier({
                              ...classifiers['KEYWORD_TYPE'],
                              values:
                                  classifiers['KEYWORD_TYPE']?.values?.filter(
                                      (v) => v.parameters?.['globalLFHub'] === true
                                  ) ?? null,
                          })
                        : [],
                },
                {
                    type: 'checkboxgroup',
                    label: t('law-firms.filters.contact-type'),
                    selector: 'partyType',
                    group: 'law-firms.filters.contact',
                    filters: [
                        {
                            label: t('contacts.types.company'),
                            argument: 'COMPANY',
                            defaultSelected: true,
                        },
                        {
                            label: t('contacts.types.person'),
                            argument: 'PERSON',
                        },
                    ],
                },
                {
                    type: 'checkboxgroup',
                    label: t('law-firms.filters.relationship-type'),
                    selector: 'relationshipTypeCode',
                    group: 'law-firms.filters.contact',
                    multiple: true,
                    filters: classifiers
                        ? mapToFilterOptions(
                              [RelationshipTypeCode.CLIENT, RelationshipTypeCode.LEAD, RelationshipTypeCode.NON_CLIENT],
                              classifiers['RELATIONSHIP_TYPE']
                          )
                        : [],
                },
                {
                    type: 'text',
                    label: t('law-firms.filters.city'),
                    selector: 'id',
                    comperator: '=contactcity=',
                    group: 'law-firms.filters.contact',
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('law-firms.filters.contact-relationship-partner'),
                    selector: 'id',
                    comperator: '=contactrelationshippartner=',
                    group: 'law-firms.filters.contact',
                    paginated: {
                        fetch: useLazyGetEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('law-firms.filters.contact-relationship-manager'),
                    selector: 'id',
                    comperator: '=contactrelationshipmanager=',
                    group: 'law-firms.filters.contact',
                    paginated: {
                        fetch: useLazyGetEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'multiselect',
                    label: t('law-firms.filters.contact-segment'),
                    selector: 'segmentTypeCode',
                    group: 'law-firms.filters.contact',
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers['SEGMENT_TYPE']) : [],
                },
                {
                    type: 'multiselect',
                    label: t('law-firms.filters.contact-service-level'),
                    selector: 'id',
                    comperator: '=contactservicelevel=',
                    group: 'law-firms.filters.contact',
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers['SERVICE_LEVEL_TYPE']) : [],
                },
                // Group section filters
                {
                    type: 'text',
                    label: t('law-firms.filters.group-name'),
                    selector: 'id',
                    comperator: '=groupname=',
                    group: 'law-firms.filters.group',
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('law-firms.filters.group-relationship-partner'),
                    selector: 'id',
                    comperator: '=grouprelationshippartner=',
                    group: 'law-firms.filters.group',
                    paginated: {
                        fetch: useLazyGetEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('law-firms.filters.group-relationship-manager'),
                    selector: 'id',
                    comperator: '=grouprelationshipmanager=',
                    group: 'law-firms.filters.group',
                    paginated: {
                        fetch: useLazyGetEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'multiselect',
                    label: t('law-firms.filters.group-segment'),
                    selector: 'id',
                    comperator: '=groupsegment=',
                    group: 'law-firms.filters.group',
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers['SEGMENT_TYPE']) : [],
                },
                {
                    type: 'multiselect',
                    label: t('law-firms.filters.group-service-level'),
                    selector: 'id',
                    comperator: '=groupservicelevel=',
                    group: 'law-firms.filters.group',
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers['SERVICE_LEVEL_TYPE']) : [],
                },
            ],
            groupTooltips: GROUP_TOOLTIPS,
            searchBarSelectors: [
                { selector: 'name' },
                { selector: 'partyNumber' },
                { selector: 'mainEmail' },
                { selector: 'mainPhone', comparator: '=phonelk=' },
                { selector: 'mainMobile', comparator: '=phonelk=' },
                { selector: 'partners.employee.fullName' },
                { selector: 'managers.employee.fullName' },
            ],
        }),
        [classifiers]
    );

    if (isLoadingClassifiers) {
        return <Loader />;
    }

    return (
        <ListTableWithFilters<LawFirmListItemDto>
            getRowHref={getLawFirmHref}
            // Managed v8 engine for the weighted-width layout; column picker / reorder / resize
            // stay OFF (flags default false) — this table never had them.
            columnConfigId="law-firms"
            columns={columns}
            renderFullRow={renderFullRow}
            data={data}
            isLoading={isFetching}
            pluralTextId="law-firms.law-firms"
            singularTextId="law-firms.law-firm"
            noEntriesId="law-firms.no-law-firms-found"
            noEntriesBodyId="law-firms.empty.body"
            noEntriesIcon={AccountBalanceOutlinedIcon}
            fetchData={fetchData}
            filterConfig={filterConfig}
            rowsPerPage={50}
        />
    );
};

export default LawFirmsTable;
